(() => {
  const normalise = (value = '') =>
    String(value)
      .toLowerCase()
      .replace(/grey/g, 'gray')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const debounce = (fn, wait = 180) => {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => fn(...args), wait);
    };
  };

  const escapeHtml = (value = '') =>
    String(value).replace(/[&<>"']/g, (char) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;'
    }[char]));

  const expandTattooQuery = (raw) => {
    let query = String(raw || '').trim();

    // Standardise common cartridge notation without changing the visible input.
    query = query
      .replace(/\b(\d{1,2})\s*rl\b/ig, '$1RL')
      .replace(/\b(\d{1,2})\s*rs\b/ig, '$1RS')
      .replace(/\b(\d{1,2})\s*(?:cm|curved\s*mag(?:num)?)\b/ig, '$1CM')
      .replace(/\b(\d{1,2})\s*(?:m1|mag(?:num)?)\b/ig, '$1M1');

    return query;
  };

  const money = (amount) => {
    if (amount === null || amount === undefined || amount === '') return '';
    const number = Number(amount);
    if (Number.isNaN(number)) return String(amount);

    // Shopify predictive JSON can return pounds or pence depending on context.
    const pounds = number > 9999 ? number / 100 : number;
    return new Intl.NumberFormat(
      document.documentElement.lang || 'en-GB',
      { style: 'currency', currency: 'GBP' }
    ).format(pounds);
  };

  const productImage = (product) =>
    product.featured_image?.url ||
    product.featured_image ||
    product.image?.url ||
    product.image ||
    '';

  const scoreProduct = (product, query) => {
    const term = normalise(query);
    const title = normalise(product.title);
    const vendor = normalise(product.vendor);
    const type = normalise(product.type || product.product_type);
    const variants = normalise(
      (product.variants || []).map((variant) =>
        `${variant.title || ''} ${variant.sku || ''}`
      ).join(' ')
    );

    let score = 0;
    if (title === term) score += 100;
    if (title.startsWith(term)) score += 55;
    if (title.includes(term)) score += 35;
    if (vendor.includes(term)) score += 25;
    if (variants.includes(term)) score += 22;
    if (type.includes(term)) score += 15;

    term.split(' ').filter(Boolean).forEach((word) => {
      if (title.includes(word)) score += 7;
      if (variants.includes(word)) score += 5;
      if (vendor.includes(word)) score += 3;
    });

    return score;
  };

  const renderResults = (products, query) => {
    if (!products.length) {
      return `
        <div class="sos-smart-search__empty">
          <strong>No matching products yet</strong>
          <span>Press Enter to search the full catalogue.</span>
        </div>`;
    }

    const sorted = [...products]
      .sort((a, b) => scoreProduct(b, query) - scoreProduct(a, query))
      .slice(0, 8);

    return `
      <div class="sos-smart-search__topline">
        <span>Suggested products</span>
        <small>${sorted.length} shown</small>
      </div>
      <div class="sos-smart-search__list">
        ${sorted.map((product) => {
          const image = productImage(product);
          const price = money(
            product.price_min ??
            product.price ??
            product.selected_or_first_available_variant?.price
          );

          return `
            <a class="sos-smart-search__item" role="option" href="${escapeHtml(product.url)}">
              <span class="sos-smart-search__image">
                ${image ? `<img src="${escapeHtml(image)}" alt="" loading="lazy">` : ''}
              </span>
              <span class="sos-smart-search__copy">
                ${product.vendor ? `<small>${escapeHtml(product.vendor)}</small>` : ''}
                <strong>${escapeHtml(product.title)}</strong>
                ${product.type || product.product_type
                  ? `<em>${escapeHtml(product.type || product.product_type)}</em>`
                  : ''}
              </span>
              <span class="sos-smart-search__price">${escapeHtml(price)}</span>
            </a>`;
        }).join('')}
      </div>
      <a class="sos-smart-search__all" href="/search?type=product&q=${encodeURIComponent(query)}">
        View all results <span>→</span>
      </a>`;
  };

  const initialiseSearch = (form) => {
    if (form.dataset.smartSearchReady === 'true') return;
    form.dataset.smartSearchReady = 'true';

    const input = form.querySelector('[data-smart-search-input]');
    const results = form.querySelector('[data-smart-search-results]');
    const endpoint = form.dataset.predictiveUrl || '/search/suggest';

    if (!input || !results) return;

    let controller;

    const close = () => {
      results.hidden = true;
      results.innerHTML = '';
      input.setAttribute('aria-expanded', 'false');
    };

    const search = debounce(async () => {
      const raw = input.value.trim();
      if (raw.length < 2) {
        close();
        return;
      }

      controller?.abort();
      controller = new AbortController();

      results.hidden = false;
      results.innerHTML = '<div class="sos-smart-search__loading">Searching StudioOS…</div>';
      input.setAttribute('aria-expanded', 'true');

      const query = expandTattooQuery(raw);
      const url = new URL(
        endpoint.endsWith('.json') ? endpoint : `${endpoint}.json`,
        window.location.origin
      );

      url.searchParams.set('q', query);
      url.searchParams.set('resources[type]', 'product');
      url.searchParams.set('resources[limit]', '10');
      url.searchParams.set('resources[options][unavailable_products]', 'last');
      url.searchParams.set(
        'resources[options][fields]',
        'title,product_type,vendor,variants.title,variants.sku'
      );

      try {
        const response = await fetch(url, {
          signal: controller.signal,
          headers: { Accept: 'application/json' }
        });

        if (!response.ok) throw new Error('Search unavailable');

        const data = await response.json();
        const products = data?.resources?.results?.products || [];
        results.innerHTML = renderResults(products, query);
      } catch (error) {
        if (error.name === 'AbortError') return;
        results.innerHTML = `
          <div class="sos-smart-search__empty">
            <strong>Search is temporarily unavailable</strong>
            <span>Press Enter to search normally.</span>
          </div>`;
      }
    }, 170);

    input.addEventListener('input', search);
    input.addEventListener('focus', () => {
      if (input.value.trim().length >= 2 && results.innerHTML) {
        results.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      }
    });

    form.addEventListener('submit', () => {
      input.value = expandTattooQuery(input.value);
    });

    document.addEventListener('click', (event) => {
      if (!form.contains(event.target)) close();
    });

    input.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        close();
        input.blur();
      }
    });
  };

  const initialise = (scope = document) => {
    scope.querySelectorAll('[data-smart-search]').forEach(initialiseSearch);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialise(), { once: true });
  } else {
    initialise();
  }

  document.addEventListener('shopify:section:load', (event) => initialise(event.target));
})();
